import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerAgreementTracker() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [search, setSearch] = useState('');
  const [companies, setCompanies] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus)  params.set('status', filterStatus);
    if (filterCompany) params.set('company_id', filterCompany);
    const res = await API(`/api/owner/agreement-tracker?${params}`);
    const d = await res.json();
    if (!res.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
    const rows = d.agreements || [];
    setAgreements(rows);
    const uniq = [...new Map(rows.map(r => [r.company_id, { id: r.company_id, name: r.company_name }])).values()];
    setCompanies(uniq);
    setLoading(false);
  }, [filterStatus, filterCompany]);

  useEffect(() => { load(); }, [load]);

  const filtered = agreements.filter(a =>
    !search ||
    (a.introducer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.user_email || '').toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    total:        filtered.length,
    signed:       filtered.filter(a => a.effective_status === 'signed').length,
    pending:      filtered.filter(a => a.effective_status === 'pending_review').length,
    not_started:  filtered.filter(a => a.effective_status === 'not_started').length,
    waived:       filtered.filter(a => a.effective_status === 'waived').length,
  };

  const statusConfig = {
    signed:        { label: 'Signed',       bg: '#d1fae5', text: '#065f46' },
    pending_review:{ label: 'Pending Review', bg: '#fef3c7', text: '#92400e' },
    not_started:   { label: 'Not Started',  bg: '#fee2e2', text: '#991b1b' },
    waived:        { label: 'Waived',        bg: '#ede9fe', text: '#5b21b6' },
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Agreement Tracker</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>All introducer agreements across the platform</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total',       value: counts.total,       color: '#6b7280' },
            { label: 'Signed',      value: counts.signed,      color: '#10b981' },
            { label: 'Pending',     value: counts.pending,     color: '#f59e0b' },
            { label: 'Not Started', value: counts.not_started, color: '#ef4444' },
            { label: 'Waived',      value: counts.waived,      color: '#8b5cf6' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '14px 20px', borderLeft: `4px solid ${s.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            placeholder="Search introducer or user…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle}>
            <option value="">All Statuses</option>
            <option value="signed">Signed</option>
            <option value="pending_review">Pending Review</option>
            <option value="not_started">Not Started</option>
            <option value="waived">Waived</option>
          </select>
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={inputStyle}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Introducer Org', 'Company', 'Portal User', 'Status', 'Template', 'Signed / Waived', 'Waiver Note'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No agreements found</td></tr>
                ) : filtered.map(a => {
                  const cfg = statusConfig[a.effective_status] || { label: a.effective_status, bg: '#f3f4f6', text: '#6b7280' };
                  return (
                    <tr key={a.introducer_id + (a.agreement_id || '')} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{a.introducer_name}</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{a.contact_email}</div>
                      </td>
                      <td style={td}>{a.company_name}</td>
                      <td style={td}>
                        {a.user_name ? (
                          <>
                            <div>{a.user_name}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{a.user_email}</div>
                          </>
                        ) : <span style={{ color: '#9ca3af' }}>No user yet</span>}
                      </td>
                      <td style={td}>
                        <span style={{ background: cfg.bg, color: cfg.text, padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600 }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td style={td}>{a.template_name || '—'}</td>
                      <td style={td}>
                        {a.signed_at
                          ? new Date(a.signed_at).toLocaleDateString('en-GB')
                          : a.agreement_waived_at
                            ? `Waived ${new Date(a.agreement_waived_at).toLocaleDateString('en-GB')}`
                            : '—'}
                      </td>
                      <td style={{ ...td, maxWidth: '220px' }}>
                        {a.agreement_waiver_note
                          ? <span style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>{a.agreement_waiver_note}</span>
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' };
const th = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
